import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import SwiperCore, { Navigation, Pagination, Swiper, Thumbs } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

import { ImageB2c } from '../../model/image.model';
import { DIALOG_SIZE } from '../../const/dialog-sizes.const';
import { ImageDialogComponent } from './image-dialog/image-dialog.component';
import { GalleryService } from './gallery.service';
import { DialogService } from '../../../core/services/dialog.service';
import { ProductLabel } from '../../model/product-label.model';
import { LayoutService } from '../../../core/services/layout.service';

SwiperCore.use([Thumbs, Navigation, Pagination]);

@Component({
  selector: 'raf-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GalleryService],
})
export class GalleryComponent implements OnChanges {
  @Input()
  images?: ImageB2c[];

  @Input()
  infiniteSlider = true;

  @Input()
  labels?: ProductLabel[] | null;

  @Input()
  alternativeText = '';

  @Output()
  imageChange = new EventEmitter<ImageB2c | undefined>();

  @ViewChild(SwiperComponent, { static: false })
  swiperComponent?: SwiperComponent;
  thumbSwiper?: Swiper;

  image$ = new BehaviorSubject<ImageB2c | undefined>(undefined);
  thumbnails$ = new BehaviorSubject<ImageB2c[]>([]);
  isMobile$: Observable<boolean>;

  constructor(
    public dialog: DialogService,
    private galleryService: GalleryService,
    private layoutService: LayoutService
  ) {
    this.image$ = galleryService.image$;
    this.thumbnails$ = galleryService.thumbnails$;
    this.isMobile$ = this.layoutService.mdDown$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['images']) {
      this.initGallery();
    }
  }

  initGallery(): void {
    if (!this.images?.length) {
      return;
    }

    this.galleryService.setInfiniteSlider(this.infiniteSlider);
    this.galleryService.setThumbnails(this.images);
    this.galleryService.next();
  }

  /**
   * Sets new preview (actual image) and emit change
   */
  next(): void {
    this.imageChange.emit(this.galleryService.next());
  }

  /**
   * Sets new preview (actual image) and emit change
   */
  previous(): void {
    this.imageChange.emit(this.galleryService.previous());
  }

  /**
   * Sets new preview (actual image) and emit change
   */
  selectImage(image: ImageB2c, index: number): void {
    this.swiperComponent?.swiperRef?.slideTo(index);
    this.imageChange.emit(this.galleryService.selectImage(image));
  }

  openDialog(): void {
    this.dialog.open(ImageDialogComponent, {
      panelClass: 'mat-image-dialog',
      maxWidth: DIALOG_SIZE.large,
      data: {
        galleryService: this.galleryService,
        alternativeText: this.alternativeText
      },
    });
  }

  maximizeImage(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.galleryService.image) {
      return;
    }
    this.openDialog();
  }

  onSlideChange($event: [swiper: Swiper]) {
    const activeImageIdx = $event[0].activeIndex;
    const activeImage = this.thumbnails$.value[activeImageIdx];
    this.imageChange.emit(this.galleryService.selectImage(activeImage));
  }

  onThumbSwiperInit($event: Swiper) {
    this.thumbSwiper = $event;
  }
}
